// ProductId.jsx
import React from 'react';
import { useLoaderData } from 'react-router';

import UniversalComponent from '../component/UniversalComponent.jsx';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';

import { MERGE_OPERATION_DATA } from '../ActionTypes.js';
import { prepareProductPageMeta } from '../resource/prepareProductPageMeta.js';
import { createInitialStoreWithRequest } from '../createInitialStore.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import getMetaTagsForHelmet from '../resource/getMetaTagsForHelmet.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { fetchFeedTotalCount } from '../resource/fetchFeedTotalCount.js';

import fetchProductDetail from '../action/fetchProductDetail.js';
import setDefaultShopCurrency from '../action/setDefaultShopCurrency.js';

import getProductData from '../selector/getProductData.js';
import getOperationData from '../selector/getOperationData.js';
import getDiamondPackData from '../selector/getDiamondPackData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';

import useSSRDataMerge from '../hook/useSSRDataMerge.js';

import {
  PUBLIC_PRODUCT_URL_PATH,
  MESSAGE_PACK_FEED_ALIAS,
  SUPPORTED_LANGUAGES,
} from '../RemoteConfigKeys.js';
import { SHOP_ID, ProductType } from '../resource/shopConstants.js';
import getDecimalFactorByCurrency from '../resource/getDecimalFactorByCurrency.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('ProductId loader()');
  const errorLog = legacySSRError.extend('ProductId loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });
    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Page actions
    const tasks = [];
    const feedName = getRemoteConfigData(
      store.getState(),
      MESSAGE_PACK_FEED_ALIAS,
      undefined,
      {
        PRODUCT_ID: params.productId,
      }
    );

    // Fetch product detail
    await store.dispatch(
      fetchProductDetail({
        productId: params.productId,
        httpProxyHeaders,
      })
    );

    // Check if product has messages using HEAD request (lightweight)
    const messageCount = await fetchFeedTotalCount({
      feedNameWithQuery: feedName,
      httpProxyHeaders,
    });

    const productType = getProductData(
      store.getState(),
      [params.productId],
      'type'
    );
    const currencies =
      getProductData(store.getState(), [params.productId], 'currencies') || [];
    const currencySkuId = getProductData(
      store.getState(),
      [params.productId],
      'currencySkuId'
    );

    // Set shop ID based on product type
    const setUsingShopId = () => {
      if (!productType) {
        return;
      }
      return store.dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['shop'],
          data: {
            usingShopId:
              ProductType.MESSAGEPACKS === productType
                ? SHOP_ID.messagePack
                : SHOP_ID.addvalue,
          },
        },
      });
    };

    await setUsingShopId();

    const shopId = getOperationData(store.getState(), ['shop'], 'usingShopId');

    // Set default currency
    const setDefaultShopCurrencyByProductId = () => {
      if (!params.productId || !currencies.length) {
        return;
      }
      return store.dispatch(
        setDefaultShopCurrency({ productId: params.productId })
      );
    };

    await setDefaultShopCurrencyByProductId();

    const currency = getOperationData(store.getState(), ['shop'], 'currency');

    // Set selected SKU ID
    const setSelectedSkuId = () => {
      const skuId = currencySkuId?.[currency];
      if (!skuId || !shopId) {
        return;
      }
      return store.dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath: ['shop', shopId],
          data: {
            selectedDiamondPackId: skuId,
          },
        },
      });
    };

    tasks.push(setSelectedSkuId());

    await Promise.all(tasks);

    // Get final state after all operations
    const finalState = store.getState();
    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    // If no messages, return noindex
    if (messageCount === 0) {
      log('no messages found, returning noindex');

      const hostname = request?.headers?.get('host');

      const title = i18nInstance.t('seo_product_title', {
        ns: TranslationNamespace.SEO,
      });
      const description = i18nInstance.t('seo_product_description', {
        ns: TranslationNamespace.SEO,
      });

      const meta = getMetaTagsForHelmet({
        title,
        description,
        hostname,
        ogType: 'website',
        twitterCard: 'summary',
        robots: ['noindex', 'follow'],
      });

      return {
        data: {
          store: {
            products: storeData.products,
            diamondPacks: storeData.diamondPacks,
          },
        },
        params,
        meta,
        staticContext: {
          requestHeaders: {
            hostname: hostname || 'swag.live',
          },
        },
      };
    }

    // Get product data for meta tags
    const selectedSkuId = getOperationData(
      finalState,
      ['shop', shopId],
      'selectedDiamondPackId'
    );
    const secondaryBadge =
      getDiamondPackData(finalState, selectedSkuId, 'secondaryBadge') || [];
    const [discountKey, discountValue] = secondaryBadge;
    const price = getDiamondPackData(finalState, selectedSkuId, 'price');
    const decimalFactor = getDecimalFactorByCurrency({ currency });
    const productNbf = getProductData(finalState, [params.productId], 'nbf');
    const productExp = getProductData(finalState, [params.productId], 'exp');
    const skuIds =
      getProductData(finalState, [params.productId], 'skuIds') || [];
    const publicProductUrlPath = getRemoteConfigData(
      finalState,
      PUBLIC_PRODUCT_URL_PATH
    );

    // Prepare meta data for SEO
    const hostname = request?.headers?.get('host') || 'swag.live';

    const purchaseText = i18nInstance.t('purchase_message_pack', {
      ns: TranslationNamespace.SHOP,
    });

    const discountText = secondaryBadge.length
      ? i18nInstance.t(
          getTranslationKey({
            key: `shop_product_badge_${discountKey}`,
          }),
          {
            discount: discountValue,
            ns: TranslationNamespace.PRODUCT,
          }
        )
      : '';

    const description = [
      purchaseText,
      `${price / decimalFactor} ${currency}`,
      discountText,
    ]
      .filter(i => i)
      .join(' ');

    const ogThumbnail = getResourceUrl({
      path: publicProductUrlPath,
      endpoint: `/${params.productId}.jpg`,
      resourceType: PUBLIC,
    }).href;

    const offers = skuIds.map(skuId => {
      const skuCurrency = getDiamondPackData(finalState, skuId, 'currency');
      const skuPrice = getDiamondPackData(finalState, skuId, 'price');
      const skuDecimalFactor = getDecimalFactorByCurrency({
        currency: skuCurrency,
      });
      return {
        '@type': 'Offer',
        priceCurrency: skuCurrency,
        price: skuPrice / skuDecimalFactor,
      };
    });

    // Build interaction statistics
    const interactionStatistic = skuIds.map(skuId => {
      const skuCurrency = getDiamondPackData(finalState, skuId, 'currency');
      const skuPrice = getDiamondPackData(finalState, skuId, 'price');
      const skuDecimalFactor = getDecimalFactorByCurrency({
        currency: skuCurrency,
      });
      return {
        type: 'BuyAction',
        price: skuPrice / skuDecimalFactor,
        priceCurrency: skuCurrency,
        endTime: productExp,
      };
    });

    // Generate canonical and alternates
    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );
    const canonicalPath = `/product/${params.productId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareProductPageMeta({
      state: finalState,
      productId: params.productId,
      productType: productType,
      description,
      imageUrl: ogThumbnail,
      releaseDate: productNbf,
      offers,
      interactionStatistic,
      hostname,
      includeJsonLd: true,
      canonical,
      alternates,
      i18n: i18nInstance,
      price,
      currency,
      decimalFactor,
      secondaryBadge,
      availableLanguages,
    });

    log('meta data prepared', { meta });

    return {
      data: {
        store: {
          products: storeData.products,
          diamondPacks: storeData.diamondPacks,
        },
      },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname,
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });

    const hostname = request?.headers?.get('host');

    const title = i18nInstance?.t?.('seo_product_title', {
      ns: TranslationNamespace.SEO,
    });
    const description = i18nInstance?.t?.('seo_product_description', {
      ns: TranslationNamespace.SEO,
    });

    // Return noindex on error
    const meta = getMetaTagsForHelmet({
      title,
      description,
      hostname,
      ogType: 'website',
      twitterCard: 'summary',
      robots: ['noindex', 'follow'],
    });

    return {
      data: null,
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: hostname || 'swag.live',
        },
      },
    };
  }
};

const ProductIdRoute = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'ProductId',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <UniversalComponent
        filename="Container_ProductDetail"
        importer={
          /* #__LOADABLE__ */ () => import('../container/ProductDetail.js')
        }
      />
    </>
  );
};

export default ProductIdRoute;
